import { notifications } from '@mantine/notifications';
import { EditPostPaidOrderPayload, RefundOrderPayload } from '@models/payload/order';
import { IErrorResponse } from '@models/response/error';
import { OrderByIdResponse, OrderUpdatedEventResponse } from '@models/response/order';
import { PaymentMethod } from '@models/ropolo';
import { ActionReducerMapBuilder, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { getOrder, refundOrderItems, updatePostPay } from '../../services/order';
import messages from '../../shared/constants/messages';
import { createAppAsyncThunk } from '../../utils/reduxUtil';
import { UpdateOrderState } from './type';
import addExtraItemToMenu from './utilities/addExtraItemById';
import addMenuItemById from './utilities/addMenuItemById';
import decreaseMenuItemById from './utilities/decreaseMenuItemById';
import removeItemById from './utilities/removeItemById';
import removeItemFromMenu from './utilities/removeItemFromMenu';
import returnOrderItem from './utilities/returnOrderItem';

const initialState: UpdateOrderState = {
  loading: false,
  submitLoading: false,
  order: undefined,
  updatedOrder: undefined,
  canAdd: false,
  removedItems: [],
  paymentType: PaymentMethod.CARD,
};

const getOrderDetailsById = createAppAsyncThunk<OrderByIdResponse, { id: string; networkStatus: boolean }>(
  'otp/getOrderDetails',
  async (input) => {
    const response = await getOrder({ id: input.id, networkStatus: input.networkStatus });
    return response;
  }
);

const refundOrderItem = createAppAsyncThunk<OrderUpdatedEventResponse, RefundOrderPayload>(
  'order/refundOrderItem',
  async (input, { rejectWithValue }) => {
    try {
      const response = await refundOrderItems({ command: input });
      notifications.show({
        message: messages.OPERATION_SUBMIT_SUCCESS,
        color: 'green',
      });
      return response;
    } catch (err) {
      return rejectWithValue((err as AxiosError)?.response?.data);
    }
  }
);

const updatePostPayHandler = createAppAsyncThunk<
  OrderUpdatedEventResponse,
  EditPostPaidOrderPayload & { networkStatus: boolean }
>('order/updatePostPayupdate', async (input, { rejectWithValue }) => {
  try {
    const response = await updatePostPay({ command: input, networkStatus: input.networkStatus });
    notifications.show({
      message: messages.OPERATION_SUBMIT_SUCCESS,
      color: 'green',
    });
    return response;
  } catch (err) {
    notifications.show({
      message: ((err as AxiosError)?.response?.data as IErrorResponse).data?.message,
      color: 'قثی',
    });

    return rejectWithValue((err as AxiosError)?.response?.data);
  }
});

const updateOrderSlice = createSlice({
  name: 'updateOrder',
  initialState,
  reducers: {
    decreaseItem: (state, action) => decreaseMenuItemById(state, action.payload),
    removeITem: (state, action) => removeItemById(state, action.payload),
    addItem: (state, action) => ({
      ...state,
      updatedOrder: addMenuItemById(state?.order?.data || {}, state?.updatedOrder || {}, state?.canAdd, action.payload),
    }),
    canAddItem: (state, action) => ({
      ...state,
      canAdd: action.payload,
    }),
    setPaymentType: (state, action) => ({
      ...state,
      paymentType: action.payload,
    }),
    removeItemFromMenu: (state, action) => removeItemFromMenu(state, action.payload),
    addExtraItemToMenu: (state, action) => addExtraItemToMenu(state, action.payload),
    returnOrderItem: (state, action) => returnOrderItem(state, action.payload),
    removeUpdateOrder: (state) => ({
      ...state,
      loading: false,
      submitLoading: false,
      order: undefined,
      updatedOrder: undefined,
      removedItems: [],
      canAdd: false,
    }),
  },
  extraReducers: (builder: ActionReducerMapBuilder<UpdateOrderState>) => {
    builder.addCase(getOrderDetailsById.pending, (state) => ({ ...state, loading: true }));
    builder.addCase(getOrderDetailsById.fulfilled, (state, action) => ({
      ...state,
      loading: false,
      order: action.payload,
      updatedOrder: action.payload?.data,
    }));
    builder.addCase(getOrderDetailsById.rejected, (state) => ({ ...state, loading: false }));

    builder.addCase(refundOrderItem.pending, (state) => ({ ...state, submitLoading: true }));
    builder.addCase(refundOrderItem.fulfilled, (state) => ({
      ...state,
      submitLoading: false,
    }));
    builder.addCase(refundOrderItem.rejected, (state) => ({ ...state, submitLoading: false }));

    builder.addCase(updatePostPayHandler.pending, (state) => ({ ...state, submitLoading: true }));
    builder.addCase(updatePostPayHandler.fulfilled, (state) => ({
      ...state,
      submitLoading: false,
    }));
    builder.addCase(updatePostPayHandler.rejected, (state) => ({ ...state, submitLoading: false }));
  },
});

export default updateOrderSlice;
export { getOrderDetailsById, refundOrderItem, updatePostPayHandler };
